import React, { useCallback, useMemo, useRef, createContext } from "react";

type BufferState = { [key: string]: boolean };

type BufferContextType = {
  canPlay: (id: string) => void;
  needsToBuffer: (id: string) => void;
  handleError: (e: any) => void;
};

export const BufferContext = createContext<BufferContextType>({
  canPlay: () => {},
  needsToBuffer: () => {},
  handleError: () => {},
});

export const BufferManager: React.FC<{
  children: React.ReactNode;
  onBuffer: () => void;
  onContinue: () => void;
  onError: (e: any) => void;
}> = ({ children, onBuffer, onContinue, onError }) => {
  const bufferState = useRef<BufferState>({});
  const currentState = useRef(false);
  const errorState = useRef<any | null>(null);

  const sendEvents = useCallback(() => {
    let previousState = currentState.current;
    currentState.current = Object.values(bufferState.current).some(Boolean);
    if (errorState.current !== null) {
      onError(errorState.current);
      return;
    }

    if (currentState.current && !previousState) {
      onBuffer();
    } else {
      onContinue();
    }

    // else if (!currentState.current && previousState) {
    //   onContinue();
    // }
  }, [onBuffer, onContinue, onError]);

  const canPlay = useCallback(
    (id: string) => {
      bufferState.current[id] = false;
      sendEvents();
    },
    [sendEvents]
  );

  const needsToBuffer = useCallback(
    (id: string) => {
      bufferState.current[id] = true;
      sendEvents();
    },
    [sendEvents]
  );

  const handleError = useCallback(
    (e: any) => {
      errorState.current = e;
      sendEvents();
    },
    [sendEvents]
  );

  const bufferEvents = useMemo(() => {
    return {
      canPlay,
      needsToBuffer,
      handleError,
    };
  }, [canPlay, needsToBuffer, handleError]);

  return <BufferContext.Provider value={bufferEvents}>{children}</BufferContext.Provider>;
};
